/**
 * uiLocations — the one registry answering "where does this live on screen?".
 *
 * Guidance copy used to only *describe* features and leave the reader to hunt.
 * The tour told a first-time user "Trace runs Follow the Money"
 * while showing a canvas with no gold on it and no hint that the control lives
 * in three different places. Three surfaces need the same answer — PlatformTour
 * bullets, the Demo Guide's "Where to find features" list, and the jargon
 * tooltips over all 51 demo walkthroughs — so the mapping lives here once
 * rather than being restated, and drifting, in each.
 *
 * Each entry carries three things, because a selector on its own is not enough:
 *
 *   selector — what to ring on screen.
 *   where    — the click path in words. Needed when the element is scrolled
 *              out of view, on another route, or inside a closed menu.
 *   reveal   — the precondition, where one exists. Many of these elements do
 *              not exist yet at the moment we describe them: gold trails need a
 *              trace to have run, the minimap appears at 30 nodes or more,
 *              every inspector tab needs a selected node. Pointing a spotlight
 *              at an absent element is worse than saying what to do first.
 *
 * Selector preference order: data-tour, data-testid, data-tab / data-action,
 * aria-label. Avoid `title=` — copy edits break those silently and the break is
 * invisible until someone clicks the affordance.
 */
export type UiSurface = 'dashboard' | 'workbench' | 'cases' | 'global' | 'fusion';

export interface UiLocation {
    /** Short name shown on the locator ring. */
    label: string;
    /** CSS selector for the element to highlight. */
    selector: string;
    /** Click path in plain English, for when the element cannot be shown. */
    where: string;
    /** What the user must do first, when the element is conditional. */
    reveal?: string;
    /** Route the element lives on. Defaults to `workbench`. */
    surface?: UiSurface;
}

const NEEDS_NODE = 'Click any wallet on the canvas first — the Node Inspector is empty until a node is selected.';

/**
 * The identity card and the Inspect/Trace row live in a block that can be
 * collapsed to give the tab content its height back, so "not found" has a second
 * cause beyond "no node selected" for anything inside it.
 */
const NEEDS_DETAILS_OPEN = `${NEEDS_NODE} If a wallet is already selected, the identity and actions block is collapsed — press "Details" at the top of the panel to open it.`;

export const UI_LOCATIONS = {
    // ── Shell (global header) ────────────────────────────────────────────
    'global-omnibar': {
        label: 'Global Investigate bar',
        selector: '[data-tour="global-omnibar"]',
        where: 'Top of the app header: paste an address, transaction hash, or UPI, then select Investigate (or press Enter).',
        reveal: 'Hidden on the Workbench — use the Workbench Omnibar there instead.',
        surface: 'dashboard',
    },
    'global-omnibar-investigate': {
        label: 'Investigate',
        selector: '[data-tour="global-omnibar-investigate"]',
        where: 'Top of the app header, immediately right of the search field.',
        reveal: 'Hidden on the Workbench — use the Workbench Omnibar there instead.',
        surface: 'dashboard',
    },

    // ── Omnibar ──────────────────────────────────────────────────────────
    'omnibar': {
        label: 'Omnibar',
        selector: '[data-tour="omnibar"]',
        where: 'Top of the Workbench, the wide search field.',
    },
    'omnibar-chain-badge': {
        label: 'Chain detection badge',
        selector: '[data-tour="omnibar-chain-badge"]',
        where: 'Inside the Omnibar, at the right-hand end of the field.',
        reveal: 'Type or paste something into the Omnibar — the badge only appears once the field has content.',
    },
    'omnibar-add': {
        label: 'Add button',
        selector: '[data-tour="omnibar"] button[aria-label="Add to canvas"]',
        where: 'The gold Add button at the right-hand end of the Omnibar.',
    },

    // ── Canvas ───────────────────────────────────────────────────────────
    'graph-canvas': {
        label: 'Graph canvas',
        // Prefer the Cytoscape host (not the outer grid that also wraps the
        // inspector) so scroll-wheel zoom and empty-space pan stay usable
        // when the tour/locator cutout is open over the map.
        selector: '[data-testid="wb-graph-canvas"]',
        where: 'The centre panel — the investigation map itself. Scroll to zoom; drag empty space to pan.',
    },
    'canvas-zoom': {
        label: 'Zoom controls',
        selector: '[data-tour="canvas-zoom"]',
        where: 'Bottom-left of the canvas: the +/- buttons, fit, and reset. Scroll-wheel zoom lives on the map itself, not these buttons.',
        reveal: 'Add at least one address to the canvas — the controls appear with the graph.',
    },
    'canvas-minimap': {
        label: 'Minimap',
        selector: '[data-tour="canvas-minimap"]',
        where: 'Bottom-right corner of the canvas.',
        reveal: 'The minimap only appears once the canvas holds 30 nodes or more, and stays hidden if you dismissed it.',
    },
    'canvas-recompute': {
        label: 'Recompute Canvas',
        selector: '[data-tour="canvas-recompute"]',
        where: 'Bottom-centre of the canvas, in the canvas tools strip.',
        reveal: 'Add addresses to the canvas first — the tools strip is hidden on an empty graph.',
    },
    'graph-find': {
        label: 'Find in graph',
        selector: '[data-tour="graph-find"]',
        where: 'Bottom-centre of the canvas, the first field in the canvas tools strip (left of Recompute). Type an address, label, entity name or tx hash: matches stay lit, everything else dims, the first match is selected. Enter cycles matches, Esc clears.',
        reveal: 'Add addresses to the canvas first — the tools strip is hidden on an empty graph.',
    },
    'canvas-layout-modes': {
        label: 'Rearrange as: Smart / Flow / Organic / Radial / Circle / Grid',
        selector: '[role="group"][aria-label="Rearrange layout"]',
        where: 'Bottom-centre of the canvas, right of Recompute. Smart auto-picks; Flow = L→R money path; Organic = dense/cycles; Radial = hub-and-spoke; Circle = every node on one ring; Grid = rows and columns.',
        reveal: 'Add addresses to the canvas first — the tools strip is hidden on an empty graph.',
    },
    'canvas-playback': {
        label: 'Fund Flow Playback',
        selector: '[data-tour="canvas-playback"]',
        where: 'Below the canvas — the Show Fund Flow Playback toggle.',
        reveal: 'Needs transfers on the canvas to replay.',
    },
    'canvas-empty-demo': {
        label: 'Start with Demo',
        selector: '[data-tour="canvas-empty-demo"]',
        where: 'Centre of an empty Workbench canvas.',
        reveal: 'Clear the canvas (or open a fresh Workbench) so no nodes are loaded.',
    },
    'canvas-legend': {
        label: 'Graph legend',
        selector: '[data-tour="canvas-legend"]',
        where: 'Upper-left of the canvas (opens from Legend in the header toolbar). Drag the header to move; resize from any edge or corner.',
        reveal: 'Click Legend in the Workbench header toolbar. The popup opens upper-left on the canvas — drag, resize, or close when done.',
    },
    'header-legend': {
        label: 'Legend toolbar button',
        selector: '[data-tour="header-legend"]',
        where: 'Workbench header toolbar — the Legend button.',
    },
    'wb-case-wallets': {
        label: 'Case wallets',
        selector: '[data-tour="wb-case-wallets"]',
        where: 'Upper-right of the Workbench canvas, the Case wallets list.',
        reveal: 'Open the Workbench from a case (Cases → a case → Workbench, or ?case_id= on the URL). The list is the case graph, not a new fetch.',
    },
    'wb-promote-to-case': {
        label: 'Promote to case',
        selector: '[data-tour="wb-promote-to-case"]',
        where: 'Node Inspector → Analysis → Targeted Node Intelligence, the Promote to case button at the bottom of the run card.',
        reveal: 'Open the Workbench from a case, select a wallet, open Analysis, and finish a Targeted Node Intelligence run. Disabled with a reason when there is no case.',
    },
    'canvas-graph-summary': {
        label: 'AI graph summary',
        selector: '[data-tour="canvas-graph-summary"]',
        where: 'Bottom-right of the canvas, directly above the Keyboard shortcuts icon. One click summarizes the whole graph.',
        reveal: 'Appears once the canvas has nodes. Click to generate; when it finishes, Node Inspector → Analysis → Macro Graph Analysis opens with the summary.',
    },
    'canvas-shortcuts': {
        label: 'Keyboard shortcuts',
        selector: '[data-tour="canvas-shortcuts"]',
        where: 'Bottom-right of the canvas, or press ? / F1 anywhere.',
    },

    // ── Right-click menus ────────────────────────────────────────────────
    // These exist only while open, so `reveal` does the real work: it names the
    // gesture that opens the menu and what is inside, which is what someone
    // reading "right-click a node to run a trace" actually needs.
    'node-context-menu': {
        label: 'Node right-click menu',
        selector: '[data-testid="node-context-menu"]',
        where: 'Opens at the cursor when you right-click a wallet on the canvas.',
        reveal: 'Right-click any wallet to open it: Expand Connections, Trace Forward, Trace Backward, Investigate, Copy Address, View on Explorer, Flag for Review, Mark Suspicious, Add to Case, Pin, Stash, View Details.',
    },
    'edge-context-menu': {
        label: 'Edge (transfer) right-click menu',
        selector: '[data-testid="edge-context-menu"]',
        where: 'Opens at the cursor when you right-click a transfer line on the canvas.',
        reveal: 'Right-click any transfer line to open it: Select / Deselect as evidence, De-emphasize, Clear, Copy Tx Hash, Stash Edge.',
    },

    // ── Header toolbar ───────────────────────────────────────────────────
    'header-filters': {
        label: 'Filters',
        selector: '[data-tour="header-filters"]',
        where: 'Header toolbar, directly right of the Omnibar. Shortcut: F.',
        reveal: 'The Filters button appears once the canvas has something to filter.',
    },
    'graph-filters': {
        label: 'Graph filters',
        selector: '[data-testid="wb-filters-panel"]',
        where: 'Opens from the Filters button beside the Omnibar (shortcut F). Try amount, risk, and entity chips — matching items stay on the canvas.',
        reveal: 'Needs addresses on the canvas. Opens automatically when you locate this from the tour.',
    },
    'header-show-all': {
        label: 'Show all nodes',
        selector: '[data-testid="wb-header-actions"] button[aria-label="Show all nodes"]',
        where: 'Header toolbar, second row — the eye icon in the canvas utilities cluster. Resets everything hidden or dimmed.',
        reveal: 'Appears once the canvas has nodes.',
    },
    'header-undo-redo': {
        label: 'Undo / Redo',
        selector: '[data-testid="wb-header-actions"] [role="group"][aria-label="Canvas utilities"]',
        where: 'Header toolbar, second row, left-hand cluster. Ctrl+Z / Ctrl+Shift+Z.',
        reveal: 'Appears once the canvas has nodes.',
    },
    'header-trace': {
        label: 'Trace',
        selector: '[data-tour="header-trace"]',
        where: 'Header toolbar, second row, Graph tracing group. Shortcut: T. This is what draws the gold trail.',
        reveal: 'Needs an active session — add an address to the canvas first.',
    },
    'header-links': {
        label: 'Links',
        selector: '[data-tour="header-links"]',
        where: 'Header toolbar, second row, Graph tracing group. Finds paths between addresses already on the canvas.',
        reveal: 'Needs two or more addresses on the canvas.',
    },
    'header-investigate': {
        label: 'Investigate',
        selector: '[data-tour="header-investigate"]',
        where: 'Header toolbar, second row, Analysis group. Shortcut: I. Opens the Analysis tab in the Node Inspector.',
        reveal: 'Needs an active session — add an address to the canvas first.',
    },
    'header-analyze': {
        label: 'Analyze',
        selector: '[data-tour="header-analyze"]',
        where: 'Header toolbar, second row, Analysis group. Shortcut: G. Opens Macro Graph Analysis.',
        reveal: 'Needs an active session — add an address to the canvas first.',
    },

    // ── Node Inspector ───────────────────────────────────────────────────
    'node-inspector': {
        label: 'Node Inspector',
        selector: '[data-tour="node-inspector"]',
        where: 'The right-hand panel.',
        reveal: NEEDS_NODE,
    },
    'inspector-tabs': {
        label: 'Inspector tabs',
        selector: '[data-testid="wb-inspector-tabs"]',
        where: 'Top of the Node Inspector — seven pills that wrap onto a second row on a narrow panel.',
        reveal: NEEDS_NODE,
    },
    'inspector-resize': {
        label: 'Panel resize handle',
        selector: '[data-tour="inspector-resize"]',
        where: 'The left edge of the Node Inspector — drag it to widen the panel (440–780px).',
        reveal: NEEDS_NODE,
    },
    'inspector-tab-overview': {
        label: 'Overview tab',
        selector: '[data-testid="wb-inspector-tabs"] button[data-tab="overview"]',
        where: 'Node Inspector → first tab.',
        reveal: NEEDS_NODE,
    },
    'inspector-tab-parties': {
        label: 'Parties tab',
        selector: '[data-testid="wb-inspector-tabs"] button[data-tab="counterparties"]',
        where: 'Node Inspector → Parties.',
        reveal: NEEDS_NODE,
    },
    'inspector-tab-transfers': {
        label: 'Transfers tab',
        selector: '[data-testid="wb-inspector-tabs"] button[data-tab="transfers"]',
        where: 'Node Inspector → Transfers.',
        reveal: NEEDS_NODE,
    },
    'inspector-tab-intel': {
        label: 'Intel tab',
        selector: '[data-testid="wb-inspector-tabs"] button[data-tab="intelligence"]',
        where: 'Node Inspector → Intel.',
        reveal: NEEDS_NODE,
    },
    'inspector-tab-analysis': {
        label: 'Analysis tab',
        selector: '[data-testid="wb-inspector-tabs"] button[data-tab="analysis"]',
        where: 'Node Inspector → Analysis. Shortcuts I and G jump straight here.',
        reveal: NEEDS_NODE,
    },
    'inspector-tab-actions': {
        label: 'Actions tab',
        selector: '[data-testid="wb-inspector-tabs"] button[data-tab="actions"]',
        where: 'Node Inspector → Actions.',
        reveal: NEEDS_NODE,
    },
    'inspector-tab-forensic': {
        label: 'Forensic tab',
        selector: '[data-testid="wb-inspector-tabs"] button[data-tab="forensic"]',
        where: 'Node Inspector → Forensic — records an exchange disclosed under subpoena.',
        reveal: NEEDS_NODE,
    },

    // ── Identity chips ───────────────────────────────────────────────────
    'identity-chips': {
        label: 'Identity chips',
        selector: '[data-tour="identity-chips"]',
        where: 'Top card of the Node Inspector, under the address.',
        reveal: NEEDS_DETAILS_OPEN,
    },
    'chip-risk': {
        label: 'Risk score chip',
        selector: '[data-tour="identity-chips"] button[title="Open risk details in Overview tab"]',
        where: 'Node Inspector identity card — the colour-coded score chip.',
        reveal: 'Shown once a risk score has been computed for the selected wallet, with the identity block open.',
    },
    'chip-sanctions': {
        label: 'OFAC chip',
        selector: '[data-tour="identity-chips"] button[title="Open sanctions screening in Intel tab"]',
        where: 'Node Inspector identity card.',
        reveal: 'Only rendered when the wallet actually hits the sanctions list — no chip means no hit. Needs the identity block open.',
    },
    'chip-nearest-exchange': {
        label: 'Nearest exchange chip',
        selector: '[data-tour="identity-chips"] button[title="Highlight exchange path on graph"]',
        where: 'Node Inspector identity card — shows the hop count to the nearest exchange.',
        reveal: 'Appears after triage or a trace has found a route to an exchange, with the identity block open.',
    },
    'chip-escalation': {
        label: 'Escalation chip',
        selector: '[data-tour="identity-chips"] button[title="Open investigation in Analysis tab"]',
        where: 'Node Inspector identity card.',
        reveal: 'Appears once an investigation has produced an escalation recommendation, with the identity block open.',
    },

    // ── Granular action row ──────────────────────────────────────────────
    'granular-actions': {
        label: 'Inspect / Trace row',
        selector: '[data-tour="granular-actions"]',
        where: 'Node Inspector, between the address and the tabs — two labelled icon groups.',
        reveal: NEEDS_DETAILS_OPEN,
    },
    'granular-follow-money': {
        label: 'Follow the Money (single action)',
        selector: '[data-tour="granular-actions"] button[data-action="follow_money"]',
        where: 'Node Inspector → Trace group → the banknote icon.',
        reveal: NEEDS_DETAILS_OPEN,
    },

    // ── Analysis tab cards ───────────────────────────────────────────────
    'analysis-auto-investigate': {
        label: 'Targeted Node Intelligence',
        selector: '[data-tour="analysis-auto-investigate"]',
        where: 'Node Inspector → Analysis tab → first card. This is Auto-Investigate.',
        reveal: 'Needs a selected wallet. Locating this opens the Analysis tab and expands this card.',
    },
    'analysis-follow-money': {
        label: 'Follow the Money card',
        selector: '[data-tour="analysis-follow-money"]',
        where: 'Node Inspector → Analysis tab → second card. Draws the gold trail to the nearest exchange.',
        reveal: 'Needs a selected wallet. Locating this opens the Analysis tab and expands this card (collapses Auto-Investigate so it is not buried below).',
    },
    'analysis-macro': {
        label: 'Macro Graph Analysis',
        selector: '[data-tour="analysis-macro"]',
        where: 'Node Inspector → Analysis tab → third card. Pattern scan across the whole graph.',
        reveal: 'Needs a selected wallet. Locating this opens the Analysis tab and expands this card.',
    },
    'analysis-holistic': {
        label: 'Holistic Multi-Node Trace',
        selector: '[data-tour="analysis-holistic"]',
        where: 'Node Inspector → Analysis tab → fourth card.',
        reveal: 'Needs a selected wallet. Locating this opens the Analysis tab and expands this card. Select two or more addresses to run it.',
    },
    'le-report': {
        label: 'View LE Report',
        selector: '[data-tour="le-report"]',
        where: 'Node Inspector → Analysis tab → Targeted Node Intelligence, at the bottom once the run finishes.',
        reveal: 'Run an investigation first — the report button appears when it completes. Locating this opens the Analysis tab.',
    },

    // ── Dashboard (home) ─────────────────────────────────────────────────
    'dashboard-section-nav': {
        label: 'Dashboard section chips',
        selector: '[data-tour="dashboard-section-nav"]',
        where: 'Dashboard home, sticky chips under the header: Overview, Quick Access, Cases, Start here.',
        surface: 'dashboard',
        reveal: 'Open Dashboard / Home from the left sidebar.',
    },
    'dashboard-stats': {
        label: 'Dashboard overview stats',
        selector: '[data-tour="dashboard-stats"]',
        where: 'Dashboard home, first row of tiles (cases, entities, watchlist).',
        surface: 'dashboard',
        reveal: 'Open Dashboard / Home from the left sidebar.',
    },
    'dashboard-start-here': {
        label: 'Start here demos',
        selector: '[data-tour="dashboard-start-here"]',
        where: 'Dashboard home, the guided Start here block, then Open demo library for the rest.',
        surface: 'dashboard',
        reveal: 'Open Dashboard / Home. If you dismissed Start here, use Open demo library, or reset the dismiss by clearing cryptoflow-start-here-dismissed.',
    },
    'dashboard-demos': {
        label: 'Demo library catalog',
        selector: '[data-tour="dashboard-demos"]',
        where: 'Left sidebar → Demos, or Dashboard → Open demo library. Graph Legend and the scenario catalog (featured by default; Show all expands the rest).',
        surface: 'dashboard',
        reveal: 'Open Demos from the left sidebar, or click Open demo library on Dashboard home.',
    },
    'dashboard-faq': {
        label: 'Crypto FAQ',
        selector: '[data-tour="header-help"]',
        where: 'Top bar → Help (?) → Crypto FAQ.',
        surface: 'global',
        reveal: 'Click the question-mark Help button in the header, then Crypto FAQ. Available on every page, not only Dashboard.',
    },

    // ── Cases ────────────────────────────────────────────────────────────
    'nav-cases': {
        label: 'Cases',
        selector: '[data-tour="nav-cases"]',
        where: 'Left sidebar → Cases.',
        surface: 'global',
    },
    'nav-documentation': {
        label: 'Documentation',
        selector: '[data-tour="nav-documentation"]',
        where: 'Left sidebar → Documentation (below Investigate / Intelligence / Analysis groups).',
        surface: 'global',
    },
    'documentation-glossary': {
        label: 'Glossary',
        selector: '[data-tour="documentation-glossary"]',
        where: 'Left sidebar → Documentation → Glossary section.',
        surface: 'global',
        reveal: 'Open Documentation, then click Glossary.',
    },
    'documentation-new-features': {
        label: 'New Features',
        selector: '[data-tour="documentation-new-features"]',
        where: 'Left sidebar → Documentation → New Features section.',
        surface: 'global',
        reveal: 'Open Documentation, then click New Features.',
    },
    'cases-csv-import': {
        label: 'CSV / Excel import',
        selector: '[data-tour="cases-csv-import"]',
        where: 'Cases page → Import CSV / Excel, top right (.csv or .xlsx).',
        surface: 'cases',
    },
    'case-auto-investigate': {
        label: 'Auto-Investigate',
        selector: '[data-tour="case-auto-investigate"]',
        where: 'Cases → open a case → Case Investigations card → Auto-Investigate (top right of the card).',
        surface: 'cases',
        reveal: 'Open a case. The case needs seeds on its watchlist or in its notes.',
    },
    'case-runs': {
        label: 'Runs',
        selector: '[data-tour="case-runs"]',
        where: 'Cases → open a case → Runs card, below Case Investigations. Each run has Open report and Open canvas.',
        surface: 'cases',
        reveal: 'Open a case. Runs are listed once Auto-Investigate has been started for it.',
    },

    'case-ask-the-case': {
        label: 'Ask the case',
        selector: '[data-tour="case-ask-the-case"]',
        where: 'Cases → open a case → the "Ask the case" panel below the Next steps panel (it is also on the run report page, under the narrative).',
        surface: 'cases',
        reveal: 'Open a case that has a finished run. Officer tier or above; a case with no finished run shows no panel.',
    },
    'case-pivot-promote': {
        label: 'Promote to graph',
        selector: '[data-tour="case-pivot-promote"]',
        where: 'Ask the case → an answered question → "Rows found" table → the Promote to graph button at the end of a row.',
        surface: 'cases',
        reveal: 'Ask a question first. Only rows that name a payer and a payee carry the button; nothing reaches the graph until you press it.',
    },

    'case-open-fusion-canvas': {
        label: 'Open Fusion Canvas',
        selector: '[data-tour="case-open-fusion-canvas"]',
        where: 'Cases → open a case (it opens on its file page) → Open Fusion Canvas, first button at the top right.',
        surface: 'cases',
        reveal: 'Open a case from Cases or the Dashboard; the button is on the case file page, not on the canvas.',
    },
    'case-watch-toggle': {
        label: 'Watch this case',
        selector: '[data-tour="case-watch-toggle"]',
        where: 'Cases → open a case (file page) → top right next to Open Fusion Canvas: toggles automated re-screening on schedule.',
        surface: 'cases',
        reveal: 'Open a case from Cases or the Dashboard. Requires officer tier or above.',
    },
    'cases-card-open-canvas': {
        label: 'Open Fusion Canvas (case card)',
        selector: '[data-testid="case-card-open-canvas"]',
        where: 'Cases → the sparkle button at the bottom right of a case card goes straight to the canvas of that case; clicking the card itself opens the case file page.',
        surface: 'cases',
        reveal: 'Needs at least one case in the list.',
    },

    // ── Instagram OSINT ──────────────────────────────────────────────────
    'instagram-handle-input': {
        label: 'Instagram handle',
        selector: '[data-tour="instagram-handle-input"]',
        where: 'Left sidebar → Intelligence → Instagram leads → Step 1 → “I have the @handle”.',
        surface: 'global',
        reveal: 'Open Instagram leads (Intelligence group). Select a case, then the “I have the @handle” option.',
    },
    'instagram-seed-input': {
        label: 'Phone / UPI identity',
        selector: '[data-tour="instagram-seed-input"]',
        where: 'Intelligence → Instagram leads → Step 1 → “I only have phone, email, or UPI”.',
        surface: 'global',
        reveal: 'Open Instagram leads (Intelligence), select a case, then choose the phone/email/UPI option.',
    },
    'instagram-resolve-seed': {
        label: 'Search case records',
        selector: '[data-tour="instagram-resolve-seed"]',
        where: 'Intelligence → Instagram leads → Step 1, Search case records (next to phone/UPI). Does not search Instagram.com.',
        surface: 'global',
        reveal: 'Open Instagram leads (Intelligence), select a case, choose phone/email/UPI, and enter a value.',
    },

    // ── Fusion Canvas ────────────────────────────────────────────────────
    'fusion-palette': {
        label: 'Palette',
        selector: '[data-tour="fusion-palette"]',
        where: 'Fusion Canvas → left Palette rail (expand via the edge tab if collapsed).',
        surface: 'fusion',
        reveal: 'Open /fusion and stay on the Canvas tab.',
    },
    'fusion-court-safe': {
        label: 'Court-safe',
        selector: '[data-tour="fusion-court-safe"]',
        where: 'Fusion Canvas → top bar next to Build / Canvas / Journal → Court-safe view.',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab.',
    },
    'fusion-surface-filters': {
        label: 'Surface filters',
        selector: '[data-tour="fusion-surface-filters"]',
        where: 'Fusion Canvas → Tools → Legend → Surfaces list (click a surface to show/hide).',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab → Tools → Legend.',
    },
    'fusion-legend': {
        label: 'Fusion Graph Legend',
        selector: '[data-tour="fusion-legend"]',
        where: 'Fusion Canvas → toolbar → Tools → Legend.',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab → Tools → Legend.',
    },
    'fusion-timeline': {
        label: 'Ingestion Timeline',
        selector: '[data-tour="fusion-timeline"]',
        where: 'Fusion Canvas → right side of the graph (stage-colored dot + Ingestion Timeline label). Click to expand the bottom Play stages bar.',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab.',
    },
    'fusion-inspector': {
        label: 'Fusion Inspector',
        selector: '[data-testid="fusion-inspector-aside"]',
        where: 'Fusion Canvas → right Inspector rail (expand via edge tab or click a node).',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab.',
    },
    'fusion-aside-ledger': {
        label: 'Court-safe Chain',
        selector: '[data-tour="fusion-aside-ledger"]',
        where: 'Fusion Canvas → right rail → Court-safe Chain tab.',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas; expand the Inspector if collapsed.',
    },
    'fusion-aside-import': {
        label: 'Bank import',
        selector: '[data-tour="fusion-aside-import"]',
        where: 'Fusion Canvas → right rail → Bank import tab.',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas; expand the Inspector if collapsed.',
    },
    'fusion-aside-files': {
        label: 'Case files',
        selector: '[data-tour="fusion-aside-files"]',
        where: 'Fusion Canvas → right rail → Case files tab (after a bank statement import).',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas → Bank import a statement, or open Case files directly.',
    },
    'fusion-tab-build': {
        label: 'Build tab',
        selector: '[data-tour="fusion-tab-build"]',
        where: 'Fusion Canvas → top tabs → Build.',
        surface: 'fusion',
        reveal: 'Open /fusion.',
    },
    'fusion-tab-journal': {
        label: 'Journal tab',
        selector: '[data-tour="fusion-tab-journal"]',
        where: 'Fusion Canvas → top tabs → Journal (Replay all / Up to here / From here).',
        surface: 'fusion',
        reveal: 'Open /fusion.',
    },
    'fusion-run-rail': {
        label: 'Run rail',
        selector: '[data-tour="fusion-run-rail"]',
        where: 'Fusion Canvas → strip under the canvas (job chips for operators / evidence / Autopilot).',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab. Chips appear after you run an operator, evidence card, or Autopilot.',
    },
    'fusion-demo-library': {
        label: 'Fusion demos',
        selector: '[data-tour="fusion-demo-library"]',
        where: 'Fusion Canvas → toolbar → Demos.',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab.',
    },
    'fusion-demo-guide': {
        label: 'Demo Guide',
        selector: '[data-tour="fusion-demo-guide"]',
        where: 'Fusion Canvas → toolbar → Demo Guide (after a scenario is loaded).',
        surface: 'fusion',
        reveal: 'Open /fusion → Demos → pick a scenario.',
    },
    'fusion-tools': {
        label: 'Tools menu',
        selector: '[data-tour="fusion-toolbar"] button[aria-haspopup="menu"]',
        where: 'Fusion Canvas → toolbar → Tools (Save, Export, Legend, Reset).',
        surface: 'fusion',
        reveal: 'Open /fusion → Canvas tab.',
    },
    'fusion-level-toggle': {
        label: 'Macro / Detail view',
        selector: '[data-tour="fusion-level-toggle"]',
        where: 'Case Fusion Canvas → toolbar under the Build / Canvas / Journal tabs → Macro view / Detail view.',
        surface: 'fusion',
        reveal: 'Open a case canvas (Cases → open a case → Fusion Canvas) and stay on the Canvas tab.',
    },
    'fusion-actions-toggle': {
        label: 'Actions layer',
        selector: '[data-tour="fusion-actions-toggle"]',
        where: 'Case Fusion Canvas → toolbar under the Build / Canvas / Journal tabs → Actions (next to Macro view / Detail view).',
        surface: 'fusion',
        reveal: 'Open a case canvas and stay on the Canvas tab. The toggle is disabled until a case-worker run has produced seizure targets.',
    },
    'fusion-finding-review': {
        label: 'Finding review',
        selector: '[data-tour="fusion-finding-review"]',
        where: 'Case Fusion Canvas → right-hand Inspector → Seizure Target Notice panel → Finding Review, or Case Report seizure matrix row.',
        surface: 'fusion',
        reveal: 'Select a seizure target node on the Canvas to open its Notice panel in the Inspector, or view the Case Report seizure matrix.',
    },
    'case-consolidated-toggle': {
        label: 'Evidence | Consolidated',
        selector: '[data-tour="case-consolidated-toggle"]',
        where: 'Case Fusion Canvas → Canvas tab → right end of the tab row → Evidence | Consolidated. Consolidated shows the run as a few buckets with aggregated edges. A case-worker case with more than about 60 nodes opens here.',
        surface: 'fusion',
        reveal: 'Open a case canvas and stay on the Canvas tab. Consolidated needs at least one case-worker run on the case.',
    },
    'case-clear-focus': {
        label: 'Clear focus',
        selector: '[data-tour="case-clear-focus"]',
        where: 'Case Fusion Canvas → Canvas tab → Evidence view → Clear focus, just left of Evidence | Consolidated.',
        surface: 'fusion',
        reveal: 'Open a case canvas, stay on the Canvas tab, and choose Evidence. Use it after Show on evidence canvas has dimmed the other wallets.',
    },
    'case-report-open': {
        label: 'Case report',
        selector: '[data-tour="case-report-open"]',
        where: 'Case Fusion Canvas → header, right-hand side → Report (opens the printable case report in a new tab).',
        surface: 'fusion',
        reveal: 'Open a case canvas (Cases → open a case → Fusion Canvas).',
    },
    'case-canvas-attach-snapshot': {
        label: 'Attach to report',
        selector: '[data-tour="case-canvas-attach-snapshot"]',
        where: 'Case Fusion Canvas → header, right-hand side → Attach to report (stores a picture of the current board for the case report).',
        surface: 'fusion',
        reveal: 'Open a case canvas on the Canvas tab with demo surfaces off.',
    },
    'case-worker-deep-run': {
        label: 'Run case worker (deep)',
        selector: '[data-tour="case-worker-deep-run"]',
        where: 'Case Fusion Canvas → header, right-hand side → Run case worker (deep).',
        surface: 'fusion',
        reveal: 'Open a case canvas (Cases → open a case → Fusion Canvas). The case needs seeds on its watchlist or in its notes.',
    },
    'case-report-share': {
        label: 'Share report',
        selector: '[data-tour="case-report-share"]',
        where: 'Case report page, header, right-hand side: Share. It makes a link to this report, shows who opened it and revokes it.',
        surface: 'cases',
        reveal: 'Open a case report (Cases -> open a case -> Fusion Canvas -> View report).',
    },
    'case-report-sections': {
        label: 'Report sections',
        selector: '[data-tour="case-report-sections"]',
        where: 'Case report page (and the shared report), header, beside Share: Sections. One checkbox per section, the optional figures under Flows and Appendix A.1 under Appendices; the report, its navigation, Save and Print follow the choice.',
        surface: 'cases',
        reveal: 'Open a case report (Cases -> open a case -> Fusion Canvas -> View report), or open a shared report link.',
    },
    'case-report-print': {
        label: 'Print dialog',
        selector: '[data-tour="case-report-print"]',
        where: 'Case report page (and the shared report): the Print / Save as PDF button in the report toolbar opens it. Pick Report only, Ledger only or Report and ledger, see the estimated pages of each part, then Print or Save as HTML. "Print as two documents" prints the report first and the ledger on the next click.',
        surface: 'cases',
        reveal: 'Open a case report (Cases -> open a case -> Fusion Canvas -> View report), or open a shared report link, then press Print / Save as PDF in the report toolbar.',
    },
    'case-report-screen': {
        label: 'Screen identifiers',
        selector: '[data-tour="case-report-screen"]',
        where: 'Case report page, header, beside Share: Screen identifiers. It checks the case identifiers against the platform databases and fills the Screening section.',
        surface: 'cases',
        reveal: 'Open a case report (Cases -> open a case -> Fusion Canvas -> View report).',
    },
    'case-screening-suppressions': {
        label: 'Screening allow list',
        selector: '[data-tour="case-screening-suppressions"]',
        where: 'Case report page, header: Allow list, or the Screening section: Suppressed by the case team table.',
        surface: 'cases',
        reveal: 'Open a case report with screening hits (Cases -> open a case -> Fusion Canvas -> View report).',
    },
    'case-report-related': {
        label: 'Related cases panel',
        selector: '[data-tour="case-report-related"]',
        where: 'Case report page, under the header: Related cases. It lists linked cases and, for the case owner, the owner\'s admin and editor collaborators, adds or removes links.',
        surface: 'cases',
        reveal: 'Open a case report (Cases -> open a case -> Fusion Canvas -> View report). Adding and removing links is shown only to users who can edit the case (owner, owner\'s admin, super admin, editor collaborator); a viewer sees the list read-only.',
    },
    'case-review-again': {
        label: 'Review again with the larger model',
        selector: '[data-tour="case-review-again"]',
        where: 'Case report page, under the header and the case narrative, in the review line above the report: Review again with the larger model. It reviews the stored record again with the larger model, keeps both passes and shows the latest in the Review section.',
        surface: 'cases',
        reveal: 'Open a case report (Cases -> open a case -> Fusion Canvas -> View report) of a finished run whose review ran. Shown to officer tier and above.',
    },
    'case-report-claim-check': {
        label: 'Check claims',
        selector: '[data-tour="case-report-claim-check"]',
        where: 'Case report page, evidence artifacts: Check claims control and ledger view.',
        surface: 'cases',
        reveal: 'Open a case report with report-type evidence attached.',
    },
    'fusion-demo-pill': {
        label: 'Sandbox / Demo pill',
        selector: '[data-tour="fusion-demo-pill"]',
        where: 'Fusion Canvas → title bar — Sandbox until Explore demos (then Demo).',
        surface: 'fusion',
        reveal: 'Open /fusion.',
    },
    'fusion-new-case': {
        label: 'New Fusion case',
        selector: '[data-tour="fusion-new-case"]',
        where: 'Fusion sidebar → New case, or Build → Create a new case → opens the new case on its canvas (/cases/:id/canvas). Cases and the Dashboard open a case on its file page instead.',
        surface: 'fusion',
        reveal: 'Open /fusion while signed in (disabled for guests).',
    },
    'fusion-open-case': {
        label: 'Open Fusion case',
        selector: '[data-tour="fusion-open-case"]',
        where: 'Fusion sidebar → Open case → pick an LE case → Case Canvas. From Cases, a case opens on its file page, where Open Fusion Canvas opens the canvas.',
        surface: 'fusion',
        reveal: 'Open /fusion while signed in (disabled for guests).',
    },
    'fusion-clear-canvas': {
        label: 'Clear canvas',
        selector: '[data-tour="fusion-clear-canvas"]',
        where: 'Fusion Canvas → title bar → Clear (trash), or Tools → Clear canvas.',
        surface: 'fusion',
        reveal: 'Open /fusion.',
    },
    'fusion-build-path': {
        label: 'Build path chooser',
        selector: '[data-tour="fusion-build-path"]',
        where: 'Fusion Canvas → Build tab — Explore demos vs Create a new case.',
        surface: 'fusion',
        reveal: 'Open /fusion → Build (Start over if a board is already open).',
    },
    'fusion-welcome-demos': {
        label: 'Explore demos',
        selector: '[data-tour="fusion-welcome-demos"]',
        where: 'Fusion Canvas → Build → Explore demos & guides.',
        surface: 'fusion',
        reveal: 'Open /fusion → Build tab (path chooser).',
    },
    'fusion-welcome-new-case': {
        label: 'Create a new case (Build)',
        selector: '[data-tour="fusion-welcome-new-case"]',
        where: 'Fusion Canvas → Build → Create a new case.',
        surface: 'fusion',
        reveal: 'Open /fusion → Build tab (path chooser).',
    },
    'fusion-subject-link': {
        label: 'Link to a subject',
        selector: '[data-tour="fusion-subject-link"]',
        where: 'In the Node Inspector for any wallet on the case canvas: open the link form to confirm a subject with an evidence grade.',
        reveal: 'Click any wallet node on the Fusion canvas to inspect it.',
        surface: 'fusion',
    },
    'fusion-node-notes': {
        label: 'Node notes and private label',
        selector: '[data-tour="fusion-node-notes"]',
        where: 'In the Node Inspector for any node on the case canvas: view annotation history and add officer notes or set private labels.',
        reveal: 'Click any node on the Fusion canvas to inspect it.',
        surface: 'fusion',
    },
    'settings-detection-thresholds': {
        label: 'Detection thresholds',
        selector: '[data-tour="settings-thresholds-tab"]',
        where: 'Settings page, tab bar: Detection thresholds (read-only table of the values the engine runs with).',
        surface: 'global',
        reveal: 'Open Settings from the account menu; every signed-in user sees the tab.',
    },
    'settings-llm-cost': {
        label: 'Model cost',
        selector: '[data-tour="settings-llm-cost-tab"]',
        where: 'Settings page, tab bar: Model cost (month-to-date model spend against the ceiling, per-run caps, prices).',
        surface: 'global',
        reveal: 'Open Settings from the account menu; only super admins see the tab.',
    },
    'settings-scoreboard': {
        label: 'Benchmark scoreboard',
        selector: '[data-tour="settings-scoreboard"]',
        where: 'Settings page, tab bar: Scoreboard (nightly benchmark runs, readiness scores, fact totals, and regressions).',
        surface: 'global',
        reveal: 'Open Settings from the account menu; only super admins see the tab.',
    },
} satisfies Record<string, UiLocation>;

export type UiLocationKey = keyof typeof UI_LOCATIONS;

export function getUiLocation(key: UiLocationKey): UiLocation {
    return UI_LOCATIONS[key];
}

/** Resolve a location's element, or null when it is not currently rendered. */
export function findUiElement(key: UiLocationKey): HTMLElement | null {
    try {
        return document.querySelector<HTMLElement>(UI_LOCATIONS[key].selector);
    } catch {
        return null;
    }
}
